import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../Library/contexts';
import { RAZORPAY_SDK } from '../../Library/constants';
import { ForwardArrowIcon } from '../../Assets/images/svgs';
import { initiatePayment, verifyPayment, cancelPayment } from '../../APIs';
import './style.css';

const PaymentModal = ({ item, onClose, onSuccess }) => {
  const { profile } = useContext(AuthContext);
  const [step, setStep] = useState('summary');
  const [errorMsg, setErrorMsg] = useState('');
  const [rzpReady, setRzpReady] = useState(!!window.Razorpay);

  useEffect(() => {
    if (window.Razorpay) return;
    const script = document.createElement('script');
    script.src = RAZORPAY_SDK;
    script.async = true;
    script.onload = () => setRzpReady(true);
    script.onerror = () => console.warn('Failed to load Razorpay SDK');
    document.head.appendChild(script);
  }, []);

  const toPay = item.price;
  const mrp = item.originalPrice;
  const base = Math.round(toPay / 1.18);
  const gst = toPay - base;
  const discount = mrp ? mrp - base : 0;
  const planPrice = mrp || base;

  const handleProceed = () => {
    setStep('processing');
    setErrorMsg('');

    const normalizedBulkSids = (item.bulk && item.allSubs?.length)
      ? [...new Set(item.allSubs.map(s => parseInt(s.sid, 10)).filter(Number.isFinite))]
      : [];

    if (item.bulk && normalizedBulkSids.length < 2) {
      setErrorMsg('Please select at least 2 valid vehicles for bulk recharge.');
      setStep('error');
      return;
    }

    const payload = item.type === 'Warranty'
      ? { subs: item.subs, warranty: true }
      : { subs: item.subs, selectedPlan: item.planIdx, src: 'evwebapp' };

    // For bulk: include bulkSids so backend multiplies amt and flags the transaction
    if (item.bulk && normalizedBulkSids.length > 1) {
      payload.bulkSids = normalizedBulkSids;
    }

    initiatePayment(payload, (initErr, initData) => {
      if (initErr) {
        setErrorMsg('Could not create payment order. Please try again.');
        setStep('error');
        return;
      }

      const { order, tr, key: rzpKey } = initData;

      const options = {
        key: rzpKey,
        order_id: order.id,
        name: 'AJJAS',
        description: item.title,
        handler: (response) => {
          setStep('verifying');
          verifyPayment({ resp: response, tr }, (verifyErr) => {
            if (verifyErr) {
              setErrorMsg('Payment verification failed. If your amount was debited, please contact support.');
              setStep('error');
              return;
            }
            setStep('success');
            if (onSuccess) onSuccess();
          });
        },
        modal: {
          escape: false,
          backdropclose: false,
          ondismiss: () => {
            cancelPayment(tr, (err) => { if (err) console.warn('cancelPayment failed', err); });
            setErrorMsg('Payment was cancelled.');
            setStep('error');
          },
        },
        prefill: {
          name: profile?.fn || '',
          email: profile?.eml || '',
          contact: profile?.mob || '',
        },
        notes: { tr_id: tr.tr_id },
        theme: { color: '#2188ff' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  const perVeh = (n) => item.quantity > 1 ? Math.round(n / item.quantity) : null;

  return (
    <div className="pw-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !['processing', 'verifying'].includes(step)) onClose(); }}>
      <div className="pw-modal">
        <div className="pw-modal-header">
          <h2>{step === 'success' ? '' : 'Checkout'}</h2>
          {!['processing', 'verifying'].includes(step) && (
            <button className="pw-modal-close" onClick={onClose}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* ── SUMMARY ── */}
        {step === 'summary' && (
          <>
            <div className="bill-summary-box">
              <div className="bill-title">Bill Summary</div>

              <div className="bill-row">
                <span className="bill-label">
                  {perVeh(planPrice) ? `Plan Price (${item.quantity} × ₹${perVeh(planPrice)})` : 'Plan Price'}
                </span>
                <span className="bill-value">₹{planPrice}</span>
              </div>

              {discount > 0 && (
                <div className="bill-row bill-discount">
                  <span className="bill-label">
                    {perVeh(discount) ? `Discount (${item.quantity} × ₹${perVeh(discount)})` : 'Discount'}
                  </span>
                  <span className="bill-value">-₹{discount}</span>
                </div>
              )}

              <div className="bill-divider" />

              <div className="bill-row bill-total">
                <span className="bill-label">
                  {perVeh(base) ? `Total (${item.quantity} × ₹${perVeh(base)})` : 'Total'}
                </span>
                <span className="bill-value">₹{base}</span>
              </div>

              <div className="bill-row bill-gst">
                <span className="bill-label">
                  {perVeh(gst) ? `GST (18%) (${item.quantity} × ₹${perVeh(gst)})` : 'GST (18%)'}
                </span>
                <span className="bill-value">₹{gst}</span>
              </div>

              <div className="bill-row bill-topay">
                <span className="bill-label">To Pay</span>
                <span className="bill-value">₹{toPay}</span>
              </div>
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={handleProceed}
              disabled={!rzpReady}
            >
              {rzpReady ? 'Proceed to Payment' : 'Loading...'}
              {rzpReady && <ForwardArrowIcon width={16} height={16} />}
            </button>
          </>
        )}

        {/* ── PROCESSING / VERIFYING ── */}
        {(step === 'processing' || step === 'verifying') && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: 48, height: 48, margin: '0 auto 20px',
              border: '3px solid var(--pw-border-color)',
              borderTopColor: 'var(--pw-color-primary)',
              borderRadius: '50%',
              animation: 'pw-spin 0.8s linear infinite'
            }} />
            <p style={{ color: 'var(--pw-text-secondary)', fontSize: 15 }}>
              {step === 'processing' ? 'Initiating payment...' : 'Verifying payment...'}
            </p>
            <style>{`@keyframes pw-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {step === 'success' && (
          <div className="success-container">
            <div className="success-icon-wrap">
              <div className="success-circle">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <polyline
                    className="success-check"
                    points="12,21 18,27 28,14"
                    stroke="white"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="confetti c1" />
              <div className="confetti c2" />
              <div className="confetti c3" />
              <div className="confetti c4" />
            </div>
            <div className="success-title">
              {item.type === 'Subscription' ? 'Recharge Successful 🎉' : 'Warranty Extended 🎉'}
            </div>
            <p className="success-text">
              Your {item.type.toLowerCase()} has been updated successfully.
            </p>
            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: '15px' }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        )}

        {/* ── ERROR ── */}
        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <p style={{ color: 'var(--pw-text-secondary)', fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
              {errorMsg}
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="btn"
                style={{ flex: 1, padding: '12px', fontSize: 14, border: '1px solid var(--pw-border-color)', borderRadius: 'var(--pw-radius-md)', background: 'none', color: 'var(--pw-text-secondary)', cursor: 'pointer' }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, padding: '12px', fontSize: 14 }}
                onClick={() => { setStep('summary'); setErrorMsg(''); }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
